// Admin API service for sending push notifications

import { apiBaseUrl } from '../../config/env';

const API_URL = apiBaseUrl;

export interface User {
  id: string;
  name: string;
  email?: string;
  phone: string;
  photo?: string;
  businessName?: string;
  profession?: string;
  city?: string;
}

export interface SearchUsersResponse {
  success: boolean;
  users: User[];
}

export interface SendNotificationRequest {
  title: string;
  message: string;
  userIds?: string[];
  data?: { [key: string]: string };
}

export interface SendNotificationResponse {
  success: boolean;
  message: string;
  sentCount: number;
  totalTargeted: number;
}

export interface AdminStatsResponse {
  success: boolean;
  stats: {
    totalUsers: number;
    usersWithFCM: number;
    totalClubs: number;
    totalPosts: number;
  };
}

/**
 * Search users for notification recipients
 */
export const searchUsers = async (
  token: string,
  query: string = ''
): Promise<SearchUsersResponse> => {
  const response = await fetch(
    `${API_URL}/api/admin/users/search?query=${encodeURIComponent(query)}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to search users');
  }

  return response.json();
};

/**
 * Send push notification to all or selected users
 */
export const sendNotification = async (
  token: string,
  data: SendNotificationRequest
): Promise<SendNotificationResponse> => {
  const response = await fetch(
    `${API_URL}/api/admin/notifications/send`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send notification');
  }

  return response.json();
};

/**
 * Get admin statistics
 */
export const getAdminStats = async (token: string): Promise<AdminStatsResponse> => {
  const response = await fetch(
    `${API_URL}/api/admin/stats`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch admin stats');
  }

  return response.json();
};

/**
 * Toggle user's admin role
 */
export const toggleUserRole = async (token: string, userId: string): Promise<{ success: boolean; message: string; user: { id: string; name: string; role: string } }> => {
  const response = await fetch(
    `${API_URL}/api/auth/admin/toggle-role`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to toggle user role');
  }

  return response.json();
};

/**
 * Deactivate a user account
 */
export const deactivateUser = async (token: string, userId: string): Promise<{ success: boolean; message: string; user: { id: string; name: string; role: string } }> => {
  const response = await fetch(
    `${API_URL}/api/auth/admin/deactivate-user`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to deactivate user');
  }

  return response.json();
};

/**
 * Reactivate a deactivated user account
 */
export const reactivateUser = async (token: string, userId: string): Promise<{ success: boolean; message: string; user: { id: string; name: string; role: string } }> => {
  const response = await fetch(
    `${API_URL}/api/auth/admin/reactivate-user`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reactivate user');
  }

  return response.json();
};
